import React, { useState, useRef, useEffect, useMemo } from 'react'
import {
  Box,
  Button,
  Text,
  Flex,
  Spinner,
  VStack,
  HStack,
  Input,
  Badge,
} from '@chakra-ui/react'
import { FiDatabase, FiUserPlus } from 'react-icons/fi'
import { Icon } from '../ui/Icon'
import { Tooltip } from '../ui/Tooltip'
import { useTranslation } from 'react-i18next'
import { useNavigate, useLocation } from 'react-router-dom'
import { useActiveProfile } from '../../contexts/ActiveProfileContext'
import { useAuth } from '../../contexts/AuthContext'
import { ProfileAvatar } from '../ProfileAvatar'
import { toaster } from '../ui/toaster-instance'
import { profileAccessService } from '../../services/profileAccessService'

interface ProfileSwitcherProps {
  variant?: 'compact' | 'full'
  showAddButton?: boolean
}

export const ProfileSwitcher: React.FC<ProfileSwitcherProps> = ({ 
  variant = 'full',
  showAddButton = true 
}) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const { activeProfile, setActiveProfile, isLoading, allProfiles } = useActiveProfile()
  const { currentUser, isAuthReady } = useAuth()
  
  const [searchTerm, setSearchTerm] = useState('')
  const [isSwitching, setIsSwitching] = useState(false)
  const [isOpen, setIsOpen] = useState(false)
  const [profilesWithRoles, setProfilesWithRoles] = useState<Map<string, { role?: string, ownerName?: string }>>(new Map())
  const searchInputRef = useRef<HTMLInputElement>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)

  // Get most recent profiles (5-10) for switcher, sorted by lastViewed
  const recentProfilesForSwitcher = useMemo(() => {
    const sorted = [...allProfiles].sort((a, b) => {
      const aTime = a.lastViewed ? new Date(a.lastViewed).getTime() : (a.updatedAt ? new Date(a.updatedAt).getTime() : 0)
      const bTime = b.lastViewed ? new Date(b.lastViewed).getTime() : (b.updatedAt ? new Date(b.updatedAt).getTime() : 0)
      return bTime - aTime // Most recent first
    })
    // Limit to 10 most recent profiles
    return sorted.slice(0, 10)
  }, [allProfiles])

  // Filter profiles based on search term
  // If searching, show all matching profiles; otherwise show only recent 10
  const filteredProfiles = useMemo(() => {
    const profilesToSearch = searchTerm ? allProfiles : recentProfilesForSwitcher
    return profilesToSearch.filter(profile => {
      const nameMatch = profile.name?.toLowerCase().includes(searchTerm.toLowerCase()) ?? false
      const usernameMatch = profile.username?.toLowerCase().includes(searchTerm.toLowerCase()) ?? false
      return nameMatch || usernameMatch
    })
  }, [allProfiles, recentProfilesForSwitcher, searchTerm])

  const handleProfileSelect = async (profileId: string) => {
    if (profileId === activeProfile?.profileId) {
      onClose()
      navigate(`/profiles/view/${profileId}`)
      return
    }

    try {
      setIsSwitching(true)
      await setActiveProfile(profileId)
      
      // Show success notification
      const profile = allProfiles.find(p => p.profileId === profileId)
      if (profile) {
        toaster.create({
          title: `Switched to ${profile.name || 'Profile'}`,
          description: profile.username ? `You're now viewing @${profile.username}` : 'Profile switched',
          type: 'success',
          duration: 3000,
          closable: true,
        })
      }
      
      // If we're on a profile-specific route, update the URL with the new profile ID
      const currentPath = location.pathname
      if (currentPath.startsWith('/profiles/view/')) {
        navigate(`/profiles/view/${profileId}`)
      } else if (currentPath.startsWith('/profiles/edit/')) {
        navigate(`/profiles/edit/${profileId}`)
      } else if (currentPath.startsWith('/profiles/share/')) {
        navigate(`/profiles/share/${profileId}`)
      }
      // For other routes (dashboard, genotype-reports, etc.), just switching context is fine
      
      onClose()
    } catch (error) {
      console.error('Error switching profile:', error)
      toaster.create({
        title: 'Profile Switch Failed',
        description: 'Unable to switch profiles. Please try again.',
        type: 'error',
        duration: 5000,
        closable: true,
      })
    } finally {
      setIsSwitching(false)
    }
  }

  const handleAddProfile = () => {
    navigate('/profiles/add')
    setIsOpen(false)
  }

  const handleViewAllProfiles = () => {
    navigate('/profiles')
    setIsOpen(false)
  }

  const onClose = () => {
    setIsOpen(false)
    setSearchTerm('')
  }
  const onOpen = () => setIsOpen(true)

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        onClose()
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen])

  // Load role information for profiles
  useEffect(() => {
    const loadRoles = async () => {
      // Only load when auth is ready to prevent queries during view-as transitions
      if (!currentUser?.uid || !isAuthReady || allProfiles.length === 0) return

      try {
        const indices = await profileAccessService.getUserAccessibleProfiles(currentUser.uid)
        const rolesMap = new Map<string, { role?: string, ownerName?: string }>()

        indices.forEach(idx => {
          rolesMap.set(idx.profileId, {
            role: idx.role,
            ownerName: idx.ownerName
          })
        })

        setProfilesWithRoles(rolesMap)
      } catch (error) {
        console.error('Error loading role information:', error)
      }
    }

    loadRoles()
  }, [currentUser?.uid, isAuthReady, allProfiles])

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!isOpen) return

      switch (event.key) {
        case 'Escape':
          onClose()
          buttonRef.current?.focus()
          break
        case 'Tab':
          // Close dropdown on tab
          onClose()
          break
      }
    }

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown)
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [isOpen])

  const toggleDropdown = () => {
    if (isOpen) {
      onClose()
    } else {
      onOpen()
    }
  }

  if (isLoading) {
    return (
      <Flex alignItems="center" gap={2}>
        <Spinner size="sm" color="brand.500" />
        <Text fontSize="sm" color="fg.muted">Loading...</Text>
      </Flex>
    )
  }

  if (!activeProfile) {
    return (
      <Button
        size="sm"
        variant="outline"
        onClick={handleAddProfile}
      >
        <HStack gap={2}>
          <Icon name="plus" size="sm" />
          <Text>Add Profile</Text>
        </HStack>
      </Button>
    )
  }

  return (
    <Box position="relative">
      {/* Trigger Button */}
      <Tooltip label="Switch between profiles" placement="bottom">
        <Button
          ref={buttonRef}
          variant="ghost"
          onClick={toggleDropdown}
          size={variant === 'compact' ? 'sm' : 'md'}
          loading={isSwitching}
          loadingText="Switching..."
          _hover={{ bg: 'bg.subtle' }}
          _focus={{ 
            bg: 'bg.subtle',
            boxShadow: '0 0 0 2px var(--chakra-colors-brand-300)'
          }}
          _active={{ bg: 'bg.subtle' }}
          minW={variant === 'compact' ? 'auto' : '200px'}
          justifyContent="flex-start"
          aria-expanded={isOpen}
          aria-haspopup="menu"
          aria-label="Profile menu"
        >
          <Flex alignItems="center" gap={2} w="full">
            <ProfileAvatar 
              name={activeProfile.name || 'Profile'} 
              photoUrl={activeProfile.profilePic || activeProfile.photo} 
              size={variant === 'compact' ? 24 : 28} 
            />
            {variant === 'full' && (
              <Box textAlign="left" flex={1}>
                <Text fontSize="sm" fontWeight="medium" color="fg" lineClamp={1}>
                  {activeProfile.name || 'Unnamed Profile'}
                </Text>
                <Text fontSize="xs" color="fg.muted" lineClamp={1}>
                  {activeProfile.username ? `@${activeProfile.username}` : 'No username'}
                </Text>
              </Box>
            )}
            <Icon 
              name="chevron-down" 
              size="xs"
              style={{
                transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.2s'
              }}
            />
          </Flex>
        </Button>
      </Tooltip>

      {/* Dropdown Menu */}
      {isOpen && (
        <Box
          ref={dropdownRef}
          position="absolute"
          top="calc(100% + 8px)"
          left={0}
          zIndex={1000}
          bg="bg"
          borderRadius="lg"
          border="1px"
          borderColor="border"
          shadow="lg"
          minW="320px"
          maxW="320px"
          py={2}
          role="menu"
          aria-label="Profile selection menu"
        >
          <VStack align="stretch" gap={0}>
            {/* Search Input */}
            <Box p={3} borderBottom="1px" borderColor="border.muted">
              <HStack gap={2}>
                <Icon name="search" size="xs" />
                <Input
                  ref={searchInputRef}
                  placeholder="Search profiles..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  size="sm"
                  variant="outline"
                  bg="bg"
                  color="fg"
                  _placeholder={{ color: 'fg.muted' }}
                />
              </HStack>
            </Box>

            {/* Profile List */}
            <Box maxH="280px" overflowY="auto">
              {filteredProfiles.length === 0 ? (
                <Box p={3} textAlign="center">
                  <Text fontSize="sm" color="fg.muted">
                    {searchTerm ? 'No profiles found' : 'No profiles available'}
                  </Text>
                </Box>
              ) : (
                <VStack align="stretch" gap={0}>
                  {filteredProfiles.map((profile) => {
                    const isOwned = profile.ownerUid === currentUser?.uid
                    const isActive = profile.profileId === activeProfile.profileId
                    const roleInfo = profilesWithRoles.get(profile.profileId)

                    return (
                      <Button
                        key={profile.profileId}
                        variant="ghost"
                        justifyContent="flex-start"
                        onClick={() => handleProfileSelect(profile.profileId)}
                        bg={isActive ? 'bg.subtle' : 'transparent'}
                        _hover={{ bg: 'bg.muted' }}
                        _focus={{ bg: 'bg.muted' }}
                        py={2}
                        px={3}
                        h="auto"
                        borderRadius="none"
                        role="menuitem"
                        color="fg"
                      >
                        <Flex alignItems="center" gap={3} w="full">
                          <ProfileAvatar 
                            name={profile.name || 'Profile'} 
                            photoUrl={profile.profilePic || profile.photo} 
                            size={32} 
                          />
                          <Box flex={1} minW={0}>
                            <HStack gap={2} align="center" justify="space-between">
                              <HStack gap={2} align="center">
                                <Text fontSize="sm" fontWeight="medium" lineClamp={1} color="fg">
                                  {profile.name || 'Unnamed Profile'}
                                </Text>
                                {profile.hasRawdata && (
                                  <Tooltip
                                    label={
                                      profile.rawdataCount && profile.rawdataCount > 1
                                        ? t('profiles.raw_data_tooltip_multiple', { count: profile.rawdataCount })
                                        : t('profiles.raw_data_tooltip')
                                    }
                                    placement="top"
                                  >
                                    <Box
                                      display="inline-flex"
                                      alignItems="center"
                                      color="blue.400"
                                      _dark={{ color: 'blue.300' }}
                                      fontSize="xs"
                                      cursor="help"
                                    >
                                      <FiDatabase />
                                    </Box>
                                  </Tooltip>
                                )}
                                {roleInfo?.role && roleInfo.role !== 'owner' && (
                                  <Tooltip
                                    label={roleInfo.ownerName ? t('sharing.shared_profiles.shared_by', { owner: roleInfo.ownerName }) : t('profiles.shared_with_you_tooltip')}
                                    placement="top"
                                  >
                                    <Box
                                      display="inline-flex"
                                      alignItems="center"
                                      color="blue.400"
                                      _dark={{ color: 'blue.300' }}
                                      fontSize="xs"
                                      cursor="help"
                                    >
                                      <FiUserPlus />
                                    </Box>
                                  </Tooltip>
                                )}
                              </HStack>
                              {isActive && (
                                <Box color="green.400" _dark={{ color: 'green.300' }}>
                                  <Icon name="check" size="xs" />
                                </Box>
                              )}
                            </HStack>
                            <Text fontSize="xs" color="fg.muted" lineClamp={1} textAlign="left">
                              {profile.username ? `@${profile.username}` : 'No username'}
                            </Text>
                          </Box>
                        </Flex>
                      </Button>
                    )
                  })}
                </VStack>
              )}
            </Box>

            {/* Action Buttons */}
            <Box p={2} borderTop="1px" borderColor="border.muted">
              <VStack align="stretch" gap={1}>
                <Button
                  variant="ghost"
                  justifyContent="flex-start"
                  onClick={handleViewAllProfiles}
                  fontSize="sm"
                  h="auto"
                  py={2}
                  px={3}
                  borderRadius="none"
                  _hover={{ bg: 'bg.muted' }}
                  _focus={{ bg: 'bg.muted' }}
                  role="menuitem"
                  color="fg"
                >
                  <HStack gap={2}>
                    <Icon name="users" size="xs" />
                    <Text>View All Profiles</Text>
                  </HStack>
                </Button>
                {showAddButton && (
                  <Button
                    variant="ghost"
                    justifyContent="flex-start"
                    onClick={handleAddProfile}
                    fontSize="sm"
                    h="auto"
                    py={2}
                    px={3}
                    borderRadius="none"
                    _hover={{ bg: 'bg.muted' }}
                    _focus={{ bg: 'bg.muted' }}
                    role="menuitem"
                    color="fg"
                  >
                    <HStack gap={2}>
                      <Icon name="plus" size="xs" />
                      <Text>Add New Profile</Text>
                    </HStack>
                  </Button>
                )}
              </VStack>
            </Box>
          </VStack>
        </Box>
      )}
    </Box>
  )
}
